/**
 * @file        src/pages/flight/Cancellations.jsx
 * @description Cancellation List (read-only): cancelled / refund bookings.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (B2C Flight Management).
 *   - 2026-07-09 Claude — Reference parity: abbreviation legend under the table
 *                          (C/B/D/R/OB/IB), matching the legacy screen.
 *   - 2026-07-22 Claude — Removed that abbreviation legend (footNote) — the columns
 *                          use no such codes here, so it was noise on this screen.
 *   - 2026-08-10 Claude — Added title/subtitle/audience props (defaulting to the
 *                          unchanged B2C behaviour) so CorporateCancellationList.jsx
 *                          can reuse this page fixed to audience=b2b instead of
 *                          forking it — the backend now actually applies
 *                          `audience` (it previously accepted but ignored it).
 */
import ReadOnlyListPage, { fmtDate } from './ReadOnlyListPage';
import Chip from '../../components/Chip';
import { money, idCell, customerCell } from './cells';

const refundTone = (s) => (/complete|done|success/i.test(s) ? 'ok' : /pend|process/i.test(s) ? 'pend' : 'warn');

export default function Cancellations({
  title = 'Cancellation List',
  subtitle = 'B2C Flight · cancellations & refunds',
  audience = 'b2c',
} = {}) {
  return (
    <ReadOnlyListPage
      title={title}
      subtitle={subtitle}
      endpoint="/flight/cancellations"
      queryRoot={`flight-cancellations-${audience}`}
      extraParams={{ audience }}
      searchLabel="Booking Ref / PNR"
      showIndex={false}
      columns={[
        { key: 'id', label: 'Booking ID', render: idCell },
        { key: 'booking_reference', label: 'Booking Ref', render: (v) => <b>{v || '—'}</b> },
        { key: 'pnr_code', label: 'PNR', render: (v) => <span className="mono">{v || '—'}</span> },
        { key: 'customer_name', label: 'Customer', render: customerCell },
        { key: 'booking_status', label: 'Status', render: (v) => v ? <Chip tone="rej">{v}</Chip> : '—' },
        { key: 'cancellation_reason', label: 'Reason' },
        { key: 'refund_amount', label: 'Refund', render: money },
        { key: 'refund_status', label: 'Refund Status', render: (v) => v ? <Chip tone={refundTone(v)}>{v}</Chip> : '—' },
        { key: 'cancel_request_time', label: 'Requested', render: fmtDate },
      ]}
    />
  );
}
